import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Typography,
  Box,
  RadioGroup,
  Radio,
  FormControl
} from '@mui/material';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import DialogModal from '../../../components/DialogModal';
import {
  TransformedStudentDetail,
  RollOverInfo
} from '../../../types/userType';
import {
  PaymentOption,
  PaymentSchemesMaster
} from '../../../types/paymentSchemesMasterType';
import { ModeOfPayment, PaymentScheme } from '../../../types/paymentScheme';
import paymentSchemesMasterApi from '../../../apis/payments/paymentSchemesMaster';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';

import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import pallet from '../../../layouts/pallet';
import { SnackbarSeverityType } from '../../../enums';
import { isUndefined, omitBy, uniqBy } from 'lodash';
import { StyledRadioButton } from '../../common/styled';

interface PaymentSchemeDialogProps {
  open: boolean;
  onClose: () => void;
  userDetails: TransformedStudentDetail | null;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const generateDemandId = (userId: string): string =>
  `${userId}-${new Date().getTime()}`;

const PaymentSchemeDialog: React.FC<PaymentSchemeDialogProps> = ({
  open,
  onClose,
  userDetails,
  onShowToast
}) => {
  const initialAcademicYear =
    userDetails?.studentDetails?.rollOverInfo?.[0]?.academicYear || '';

  const [academicYear, setAcademicYear] = useState(initialAcademicYear);
  const [grade, setGrade] = useState('');
  const [selectedPaymentScheme, setSelectedPaymentScheme] = useState('');
  const [paymentSchemesMasterData, setPaymentSchemesMasterData] =
    useState<PaymentSchemesMaster | null>(null);
  const [loadingPaymentSchemes, setLoadingPaymentSchemes] = useState(false);

  const resetState = useCallback(() => {
    setAcademicYear(
      userDetails?.studentDetails?.rollOverInfo?.[0]?.academicYear || ''
    );
    setGrade('');
    setSelectedPaymentScheme('');
    setPaymentSchemesMasterData(null);
    setLoadingPaymentSchemes(false);
  }, [userDetails]);

  const fetchAndSetPaymentSchemesMaster = useCallback(async () => {
    if (userDetails?.studentDetails?.school && academicYear && grade) {
      setLoadingPaymentSchemes(true);
      try {
        const params = {
          school: userDetails.studentDetails.school,
          academicYear: academicYear,
          grade: grade
        };
        const data = await paymentSchemesMasterApi.fetchPaymentSchemesMaster(
          params
        );
        setPaymentSchemesMasterData(data);
      } catch (error: any) {
        console.error('Error fetching payment schemes master:', error);
        onShowToast(
          error?.errors?.[0]?.message || 'Failed to fetch payment schemes.',
          SnackbarSeverityType.error
        );
        setPaymentSchemesMasterData(null);
      } finally {
        setLoadingPaymentSchemes(false);
      }
    }
  }, [userDetails, academicYear, grade, onShowToast]);

  useEffect(() => {
    if (open) {
      fetchAndSetPaymentSchemesMaster();
    }
  }, [open, academicYear, grade, fetchAndSetPaymentSchemesMaster]);

  useEffect(() => {
    if (userDetails?.studentDetails?.rollOverInfo && academicYear) {
      const foundRollOverInfo = userDetails.studentDetails.rollOverInfo.find(
        (info) => info.academicYear === academicYear
      );
      if (foundRollOverInfo?.grade) {
        setGrade(foundRollOverInfo.grade);
      } else {
        setGrade('');
      }
    } else {
      setGrade('');
    }
  }, [academicYear, userDetails]);

  const academicYears: DropdownSelectOption[] =
    userDetails?.studentDetails?.rollOverInfo?.map((info: RollOverInfo) => ({
      value: info.academicYear,
      label: info.academicYear
    })) || [];

  const grades: DropdownSelectOption[] =
    userDetails?.studentDetails?.rollOverInfo?.map((info: RollOverInfo) => ({
      value: info.grade,
      label: info.grade
    })) || [];

  const currentRollOverInfo = userDetails?.studentDetails?.rollOverInfo?.find(
    (info) => info.academicYear === academicYear && info.grade === grade
  );

  const isNewAdmissionStudent = currentRollOverInfo?.isNewAdmission;

  const paymentOptions: PaymentOption[] = isNewAdmissionStudent
    ? (paymentSchemesMasterData?.newAdmissionPaymentOptions as PaymentOption[])
    : (paymentSchemesMasterData?.paymentOptions as PaymentOption[]);

  const getFullPaymentOption = (paymentOptions: PaymentOption[]) =>
    paymentOptions.find(
      (paymentOption) =>
        uniqBy(paymentOption.installments, 'feePeriod').length === 1
    );

  const emiOptionAdded = paymentOptions?.find(
    (option) => option.schemeName === ModeOfPayment.EMI
  );

  if (paymentSchemesMasterData?.showEmiOptionInMaster && !emiOptionAdded) {
    const fullPaymentOption = getFullPaymentOption(
      paymentOptions as PaymentOption[]
    ) as PaymentOption;
    paymentOptions?.push({
      ...fullPaymentOption,
      schemeName: ModeOfPayment.EMI
    });
  }

  useEffect(() => {
    setSelectedPaymentScheme('');
  }, [paymentOptions]);

  const isSaveDisabled =
    !academicYear || !grade || !selectedPaymentScheme || loadingPaymentSchemes;

  const handleSave = async () => {
    const paymentSchemeMaster = paymentSchemesMasterData;
    const studentDetails = userDetails?.studentDetails;

    if (!paymentSchemeMaster || !studentDetails || !selectedPaymentScheme) {
      console.error('Missing data for saving payment scheme');
      return;
    }

    const paymentSchemeData = {
      component: paymentSchemeMaster.component,
      grade: paymentSchemeMaster.grade,
      division: '',
      particulars: '',
      school: paymentSchemeMaster.school,
      session: paymentSchemeMaster.academicYear,
      scheme: selectedPaymentScheme,
      demandId: generateDemandId(studentDetails.id),
      userId: studentDetails.id
    };

    const paymentSchemeSelectedByUser: Partial<PaymentScheme>[] = [];

    const selectedPaymentOption = paymentOptions?.find(
      (option) => option.schemeName === selectedPaymentScheme
    );

    if (selectedPaymentOption) {
      selectedPaymentOption.installments.forEach((record) => {
        paymentSchemeSelectedByUser.push(
          omitBy(
            {
              ...paymentSchemeData,
              particulars: record.particulars || paymentSchemeData.particulars,
              feePeriod: record.feePeriod,
              paymentDueDate: (
                record.paymentDueDate as unknown as string
              ).split('T')[0],
              amount: record.amount,
              modeOfPayment:
                selectedPaymentOption.schemeName === ModeOfPayment.EMI
                  ? ModeOfPayment.EMI
                  : undefined
            },
            isUndefined
          )
        );
      });

      if (paymentSchemeSelectedByUser.length > 0) {
        paymentSchemeSelectedByUser[0] = {
          ...paymentSchemeSelectedByUser[0],
          generatePaymentLink: false
        };
      }
    }

    try {
      setLoadingPaymentSchemes(true);
      await paymentSchemesApis.addPaymentSchemes(paymentSchemeSelectedByUser);
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Error updating fee status:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to update fee status.',
        SnackbarSeverityType.error
      );
    } finally {
      setLoadingPaymentSchemes(false);
    }
  };

  const dialogActions = (
    <Button
      variant='contained'
      onClick={handleSave}
      sx={{ textTransform: 'none' }}
      disabled={isSaveDisabled}
    >
      Save
    </Button>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Select Payment Scheme'
      maxWidth='sm'
      actions={dialogActions}
    >
      {loadingPaymentSchemes && <OverlayLoader show={loadingPaymentSchemes} />}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            value={academicYear}
            options={academicYears}
            onChange={(e) => setAcademicYear(e.target.value as string)}
            name='academicYear'
            required
            fullWidth
            hasTopLabel
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DropdownSelect
            label='Grade'
            placeholder='Select Grade'
            value={grade}
            options={grades}
            onChange={(e) => setGrade(e.target.value as string)}
            name='grade'
            required
            fullWidth
            hasTopLabel
            disabled
          />
        </Box>
      </Box>
      <FormControl component='fieldset'>
        <Typography fontSize={13} fontWeight='bold' sx={{ mb: 1 }}>
          Payment Schemes{' '}
          <Box component='span' sx={{ color: pallet.red2 }}>
            *
          </Box>
        </Typography>
        <RadioGroup
          row
          value={selectedPaymentScheme}
          onChange={(e) => setSelectedPaymentScheme(e.target.value)}
          sx={{ gap: 2 }}
        >
          {paymentOptions?.map((option) => (
            <StyledRadioButton
              key={option.schemeName}
              value={option.schemeName}
              control={<Radio />}
              label={option.schemeName}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </DialogModal>
  );
};

export default PaymentSchemeDialog;
